import { type InferType, mixed, object, string } from "yup";

import { type Visibility } from "../../gql-generated/types";
import { NAME_MAX_CHARS } from "../model";

/**
 * UI copy of the server rule in my-lenses-graphql-service's validateSubmitWebLensInput
 * (LENS_NAME_ALLOWED_CHARACTERS), enforced there on every submit and rename. Mirrored so the form
 * can say no before the bytes upload; drift is safe because the server stays authoritative.
 */
export const LENS_NAME_ALLOWED_CHARACTERS = /^[\p{L}\p{Nd} !@#$%^&*()\-_=+[\]{};:'",<.>/?`~\\|]*$/u;

// Error CODES, not copy — the name field maps them to i18n messages (forms.md: never show raw
// Yup text to users).
export const NAME_ERROR_REQUIRED = "nameRequired";
export const NAME_ERROR_TOO_LONG = "nameTooLong";
export const NAME_ERROR_UNSUPPORTED_CHARACTERS = "nameUnsupportedCharacters";

// Required alone passes whitespace, and trimming is what the BFF submits against — so blank-after-
// trim fails as "required", not as a mysterious server rejection later.
const lensName = string()
    .label("Lens Name")
    .required(NAME_ERROR_REQUIRED)
    .test("non-blank", NAME_ERROR_REQUIRED, (value = "") => value.trim().length > 0)
    .max(NAME_MAX_CHARS, NAME_ERROR_TOO_LONG)
    .matches(LENS_NAME_ALLOWED_CHARACTERS, NAME_ERROR_UNSUPPORTED_CHARACTERS);

export const webLensSubmitFormSchema = object({
    name: lensName,
    // The dropdown offers a closed set, so membership needs no validation — the field lives in
    // the schema for the inferred form-values type.
    visibility: mixed<Visibility>().required(),
}).required();

export const webLensUpdateFormSchema = object({
    name: lensName,
}).required();

// The push preview's name uses the full submission rules, required included: the platform's only
// fallback for a missing name is the literal placeholder "Lens" (lens-studio-backend's
// DEFAULT_LENS_NAME), not a real default — so an unnamed preview is a worse product than asking.
export const webLensPushFormSchema = object({
    name: lensName,
}).required();

export type WebLensSubmitFormValues = InferType<typeof webLensSubmitFormSchema>;
export type WebLensUpdateFormValues = InferType<typeof webLensUpdateFormSchema>;
export type WebLensPushFormValues = InferType<typeof webLensPushFormSchema>;
